import React from 'react';
import { 
  CompletePipelineStatusProps,
  OverallPipelineState,
  OVERALL_PIPELINE_STATE_COLORS,
  PipelineState,
  PIPELINE_STATE_COLORS,
  PIPELINE_STATE_LABELS,
  PipelineMode
} from '../types/airflow';
import { useCompletePipelineStatus } from '../hooks';

const CompletePipelineStatus: React.FC<CompletePipelineStatusProps> = ({
  date,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 30000 // 30 seconds
}) => {
  // Use TanStack Query hook for intelligent polling and window focus management
  const {
    status,
    isLoading: loading,
    error,
    isPolling,
    refetch
  } = useCompletePipelineStatus({
    date,
    onStatusChange,
    autoRefresh,
    refreshInterval,
  });


  const getOverallStateIcon = (state: string) => {
    switch (state as OverallPipelineState) {
      case OverallPipelineState.QUEUED:
        return '⏳';
      case OverallPipelineState.RUNNING:
        return '🔄';
      case OverallPipelineState.WAITING:
        return '⏸️';
      case OverallPipelineState.SUCCESS:
        return '✅';
      case OverallPipelineState.FAILED:
        return '❌';
      case OverallPipelineState.PARTIAL_SUCCESS:
        return '⚠️';
      default:
        return '❓';
    }
  };

  const getPhaseStateIcon = (state: string) => {
    switch (state as PipelineState) {
      case PipelineState.QUEUED:
        return '⏳';
      case PipelineState.RUNNING:
        return '🔄';
      case PipelineState.SUCCESS:
        return '✅';
      case PipelineState.FAILED:
        return '❌';
      case PipelineState.SKIPPED:
        return '⏭️';
      case PipelineState.UP_FOR_RETRY:
        return '🔁';
      default:
        return '❓';
    }
  };


  if (loading) {
    return (
      <div className="complete-pipeline-status loading">
        <div className="status-header">
          <span className="status-icon">⏳</span>
          <span className="status-text">Loading complete pipeline status...</span>
        </div>
      </div>
    );
  }

  // Special handling for 404 - no pipeline run
  if (error && (error as any).status === 404) {
    return (
      <div className="complete-pipeline-status not-found">
        <div className="status-header">
          <span className="status-text">No pipeline run for this date</span>
          <button 
            className="retry-btn"
            onClick={() => refetch()}
            title="Refresh"
          >
            🔄
          </button>
        </div>
      </div>
    );
  }

  // Generic error handling for other errors
  if (error) {
    return (
      <div className="complete-pipeline-status error">
        <div className="status-header">
          <span className="status-text">⚠️ Error loading pipeline status</span>
          <button 
            className="retry-btn"
            onClick={() => refetch()}
            title="Retry"
          >
            🔄
          </button>
        </div>
        <div className="status-error">{error?.message || 'Failed to fetch complete pipeline status'}</div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="complete-pipeline-status not-found">
        <div className="status-header">
          <span className="status-icon">❓</span>
          <span className="status-text">Pipeline status not found</span>
        </div>
      </div>
    );
  }

  const overallStateColor = OVERALL_PIPELINE_STATE_COLORS[status.overall_state as OverallPipelineState] || '#6b7280';
  const normalizedMode = status.pipeline_mode as PipelineMode;
  const isFullPipelineMode = normalizedMode === PipelineMode.FULL;
  const isEtlOnlyPipelineMode = normalizedMode === PipelineMode.ETL_ONLY;
  const isPlaceholderResponse = status.pipeline_mode === 'unknown' || !status.etl_phase?.dag_run_id;

  if (isPlaceholderResponse) {
    return (
      <div className="complete-pipeline-status not-found">
        <div className="status-header">
          <span className="status-icon">📭</span>
          <span className="status-text">No pipeline run found for this date</span>
          <button
            className="retry-btn"
            onClick={() => refetch()}
            title="Refresh"
          >
            🔄
          </button>
        </div>
        <div className="status-note">Waiting for the next pipeline trigger. This view will update automatically when a run starts.</div>
      </div>
    );
  }

  return (
    <div className="complete-pipeline-status">
      {/* Overall Status */}
      <div className="status-header">
        <span className="status-icon">{getOverallStateIcon(status.overall_state)}</span>
        <span 
          className="status-text overall-status"
          style={{ color: overallStateColor }}
        >
          {status.overall_progress}
        </span>
        {/* Polling indicator */}
        {isPolling && (
          <span
            className="status-live-indicator"
            title="Polling active"
          >
            🔴
          </span>
        )}
        <button 
          className="refresh-btn"
          onClick={() => refetch()}
          title="Refresh status"
        >
          🔄
        </button>
      </div>

      {/* Pipeline Phases */}
      <div className="pipeline-phases">
        {/* ETL Phase */}
        <div className="phase-section">
          <div className="phase-header">
            <span className="phase-icon">{getPhaseStateIcon(status.etl_phase.state)}</span>
            <span className="phase-title">Data Processing: </span>
            <span 
              className="phase-state"
              style={{ 
                color: PIPELINE_STATE_COLORS[status.etl_phase.state as PipelineState] || '#6b7280'
              }}
            >
              {PIPELINE_STATE_LABELS[status.etl_phase.state as PipelineState] || status.etl_phase.state}
            </span>
          </div>
          
        </div>

        {/* Insights Phase */}
        {status.insights_phase ? (
          <div className="phase-section">
            <div className="phase-header">
              <span className="phase-icon">{getPhaseStateIcon(status.insights_phase.state)}</span>
              <span className="phase-title">Insights: </span>
              <span 
                className="phase-state"
                style={{ 
                  color: PIPELINE_STATE_COLORS[status.insights_phase.state as PipelineState] || '#6b7280'
                }}
              >
                {PIPELINE_STATE_LABELS[status.insights_phase.state as PipelineState] || status.insights_phase.state}
              </span>
            </div>
            
          </div>
        ) : isFullPipelineMode && status.etl_phase.state === 'success' ? (
          <div className="phase-section">
            <div className="phase-header">
              <span className="phase-icon">🚀</span>
              <span className="phase-title">Insights: </span>
              <span className="phase-state" style={{ color: '#10b981' }}>
                Triggered via asset
              </span>
            </div>
            <div className="phase-note" style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
              Insights pipeline runs automatically after ETL completes
            </div>
          </div>
        ) : isFullPipelineMode ? (
          <div className="phase-section pending">
            <div className="phase-header">
              <span className="phase-icon">⏸️</span>
              <span className="phase-title">Insights: </span>
              <span className="phase-state" style={{ color: '#8b5cf6' }}>
                {status.etl_phase.state === 'success' ? 'Waiting...' : 'Waiting for data...'}
              </span>
            </div>
          </div>
        ) : isEtlOnlyPipelineMode ? (
          <div className="phase-section">
            <div className="phase-header">
              <span className="phase-icon">ℹ️</span>
              <span className="phase-title">Insights: </span>
              <span className="phase-state" style={{ color: '#6b7280' }}>
                Not included in ETL-only runs
              </span>
            </div>
          </div>
        ) : (
          <div className="phase-section">
            <div className="phase-header">
              <span className="phase-title">Insights: </span>
              <span className="phase-state" style={{ color: '#f59e0b' }}>
                Unknown pipeline mode
              </span>
            </div>
          </div>
        )}
      </div>


      {/* Progress indicator for running states */}
      {['queued', 'running', 'waiting'].includes(status.overall_state) && (
        <div className="status-progress">
          <div className="progress-bar">
            <div className="progress-indicator" />
          </div>
          <div className="progress-text">
            {status.overall_progress}
          </div>
        </div>
      )}

      {/* Success message */}
      {status.overall_state === 'success' && (
        <div className="status-success-message">
          {isEtlOnlyPipelineMode
            ? '✅ ETL processing completed successfully. Data is ready for insights processing.'
            : '✨ All done! Your insights are ready.'}
        </div>
      )}

      {/* Partial success message */}
      {status.overall_state === 'partial_success' && (
        <div className="status-partial-success-message">
          ⚠️ Data processing completed, but insights had issues.
        </div>
      )}

      {/* Error message for failed states */}
      {status.overall_state === 'failed' && (
        <div className="status-error-message">
          ⚠️ Processing failed. Please try again.
        </div>
      )}
    </div>
  );
};

export default CompletePipelineStatus;
